import { Chip } from 'components/Chip';
import { DropdownSingleSelect } from 'components/Dropdown/DropdownVariants/DropdownSingleSelect';
import { InsightsCard } from 'components/InsightsCard/InsightsCard';
import { LoadingBounce } from 'components/Loading';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InsightCategory, MarketInsight } from 'utils/types';
import { EmptyState } from '../../components/EmptyState';
import { Heading } from '../../components/Heading';
import { useGlobalFilters } from '../../context/GlobalFilterProvider';
import {
  useCRMMarketInsights,
  useCRMMarketInsightsCategories,
  usePrincipals,
} from '../../hooks/queries';
import { getCurrentPrincipal } from '../../lib/currentPrincipal';
import {
  trackInsightsLinkClick,
  trackInsightsShare,
} from '../../utils/principal-service/event-endpoints';

const PUBLICATION_DATE_NEW_INSIGHT_DAYS = 30;

export const MarketInsightsTab: React.FC = () => {
  const { t } = useTranslation();
  const [isNewActive, setIsNewActive] = useState(false);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [activeCategories, setActiveCategories] = useState<
    { id: string; name: string }[]
  >([]);

  const { globalFilters } = useGlobalFilters();

  const { data: principals } = usePrincipals();
  const { currentPrincipal } = getCurrentPrincipal(principals);

  const filters = useMemo(
    () => ({
      countries: globalFilters.allCountriesSelected
        ? null
        : globalFilters.countries,
      marketSegments: globalFilters.allMarketSegmentsSelected
        ? null
        : globalFilters.marketSegments,
      categories: activeCategories,
      maxAgeInDays: isNewActive ? PUBLICATION_DATE_NEW_INSIGHT_DAYS : null,
    }),
    [
      globalFilters.allCountriesSelected,
      globalFilters.countries,
      globalFilters.allMarketSegmentsSelected,
      globalFilters.marketSegments,
      activeCategories,
      isNewActive,
    ],
  );

  const { data: crmMarketInsights, isLoading: isCrmMarketInsightsLoading } =
    useCRMMarketInsights({
      principalId: currentPrincipal.id,
      filters,
      sortOrder: sortDirection,
    });

  const { data: categories, isLoading: isCategoriesLoading } =
    useCRMMarketInsightsCategories(currentPrincipal.id);

  const handleCategories = (category: InsightCategory) => {
    if (activeCategories.includes(category)) {
      setActiveCategories((prev) => prev.filter((c) => c.id !== category.id));
    } else {
      setActiveCategories((prev) => [...prev, category]);
    }
  };

  const isNewInsight = (insight: MarketInsight) => {
    if (!insight.publishedAt) return false;
    const today = new Date();
    const diffInDays = Math.floor(
      (today.getTime() - insight.publishedAt.getTime()) / (1000 * 60 * 60 * 24),
    );
    return diffInDays < PUBLICATION_DATE_NEW_INSIGHT_DAYS;
  };

  const sortDropdownOptions = {
    desc: {
      name: t(`features:insights:market-insights:filters:sort:label`, {
        context: 'desc',
      }),
      value: 'desc',
    },
    asc: {
      name: t(`features:insights:market-insights:filters:sort:label`, {
        context: 'asc',
      }),
      value: 'asc',
    },
  };

  return (
    <>
      <div className="flex flex-col justify-between md:flex-row md:space-x-2">
        <Heading
          text={t('features:insights:market-insights:pageTitle')}
          level="h2"
        />
        <DropdownSingleSelect
          widthVariant="full-width"
          allowUnset={false}
          onChange={(option) => {
            if (option.value === 'asc' || option.value === 'desc') {
              setSortDirection(option.value);
            }
          }}
          initialSelection={sortDropdownOptions[sortDirection]}
          options={Object.values(sortDropdownOptions)}
        />
      </div>
      <div className="mb-5 mt-5 md:mb-6">
        <div className="flex flex-wrap gap-1">
          {categories?.length > 0 ? (
            <>
              <Chip
                label="All"
                active={!isNewActive && activeCategories.length === 0}
                onClick={() => {
                  setActiveCategories([]);
                  setIsNewActive(false);
                }}
              />
              <div className="mx-1 h-6 w-[1px] border-l border-gray-400"></div>
              {crmMarketInsights.some((i) => isNewInsight(i)) ? (
                <Chip
                  label="New"
                  active={isNewActive}
                  onClick={() => setIsNewActive(!isNewActive)}
                />
              ) : null}
              {!isCategoriesLoading ? (
                categories.map((category) => (
                  <Chip
                    key={category.id}
                    label={category.name}
                    active={activeCategories.includes(category)}
                    onClick={() => handleCategories(category)}
                  />
                ))
              ) : (
                <LoadingBounce />
              )}
            </>
          ) : null}
        </div>
      </div>
      <div className="mb-4 flex flex-wrap md:mb-5">
        {isCrmMarketInsightsLoading ? (
          <LoadingBounce text="Loading market insights" />
        ) : crmMarketInsights.length > 0 ? (
          crmMarketInsights.map((entry, index: number) => {
            return (
              <div className="w-full py-2" key={index}>
                <InsightsCard
                  title={entry.title}
                  categories={entry.categories.map((category) => category.name)}
                  description={
                    entry.content ||
                    t('features:insights:market-insights:default-description')
                  }
                  link={entry.link}
                  image={entry.imageUrl}
                  date={entry.publishedAt}
                  location={
                    entry.countries?.length > 0
                      ? entry.countries?.map((c) => c.name)?.join(', ')
                      : entry.region ?? 'Global'
                  }
                  newInsight={isNewInsight(entry)}
                  sustainable={false}
                  marketSegment={entry.marketSegment?.name ?? null}
                  buttonAction={() => {
                    trackInsightsLinkClick(currentPrincipal.id, entry.title);
                  }}
                  iconButtonAction={() => {
                    trackInsightsShare(currentPrincipal.id, entry.title);
                  }}
                />
              </div>
            );
          })
        ) : categories?.length > 0 ? (
          <div className="mt-4 w-full">
            <EmptyState
              message={t('features:insights:market-insights:emptyFilter')}
              hasBorder
              variant="filters"
            />
          </div>
        ) : (
          <div className="mt-4 w-full">
            <EmptyState
              message={t('features:insights:market-insights:empty')}
              hasBorder
            />
          </div>
        )}
      </div>
    </>
  );
};
